function isValidUUID(uuid) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid);
}

function detectLanguage(configLang) {
  // 1. Explicit config.lang wins
  if (configLang) return String(configLang).split('-')[0];
  // 2. <html lang>
  const htmlLang = document.documentElement.getAttribute('lang');
  if (htmlLang) return htmlLang.split('-')[0];
  // 3. <meta http-equiv="content-language"> or <meta name="language">
  const meta = document.querySelector('meta[http-equiv="content-language" i]')
            || document.querySelector('meta[name="language" i]');
  if (meta && meta.content) return meta.content.split('-')[0];
  // 4. ?lang= query on host page URL
  try {
    const q = new URLSearchParams(window.location.search).get('lang');
    if (q) return q.split('-')[0];
  } catch (_) { /* URLSearchParams unavailable */ }
  // 5. None — let the server resolve via lang cookie / Accept-Language / notice default
  return null;
}

function getApiOrigin() {
  // First priority: try to get from SDK script (privacy-sdk.min.js) if loaded from a server
  let sdkScriptSrc = '';
  
  // Try to find the privacy-sdk script specifically
  const sdkScripts = document.querySelectorAll('script[src*="privacy-sdk"]');
  if (sdkScripts.length > 0) {
    sdkScriptSrc = sdkScripts[sdkScripts.length - 1].src;
  } else if (document.currentScript) {
    sdkScriptSrc = document.currentScript.src;
  } else {
    // Fallback: get the last script tag (likely to be this SDK during initialization)
    const scripts = document.getElementsByTagName('script');
    sdkScriptSrc = scripts[scripts.length - 1].src;
  }
  
  // Check if SDK is loaded from a valid server URL
  if (sdkScriptSrc) {
    try {
      const url = new URL(sdkScriptSrc);
      if (url.protocol === 'https:' || url.protocol === 'http:') {
        console.log('PrivacySDK: Detected API origin from SDK script:', url.origin);
        return url.origin;
      }
    } catch (e) {
      console.warn('Failed to parse SDK script URL:', sdkScriptSrc);
    }
  }
  
  // Second priority: try to find the Reddot script to get the API origin
  const reddotScripts = document.querySelectorAll('script[src*="reddot.js"]');
  for (let script of reddotScripts) {
    try {
      const url = new URL(script.src);
      console.log('PrivacySDK: Detected API origin from Reddot script:', url.origin);
      return url.origin;
    } catch (e) {
      console.warn('Failed to parse Reddot script URL:', script.src);
    }
  }
  
  // Last fallback: use window location origin (might not work for cross-origin)
  console.warn('PrivacySDK: Could not determine API origin from scripts, using window.location.origin');
  return window.location.origin;
}

class PrivacySDK {
  constructor(config) {
    if (!config) {
      console.error('PrivacySDK Error: Configuration object is required');
      console.error('Example: new PrivacySDK({ domId: "privacy-container" })');
      throw new Error('Configuration object is required');
    }
    
    if (!config.domId) {
      console.error('PrivacySDK Error: domId is required in configuration');
      console.error('Example: new PrivacySDK({ domId: "privacy-container" })');
      console.error('Make sure you have a corresponding element: <div id="privacy-container"></div>');
      throw new Error('domId is required in configuration');
    }
    
    this.domId = config.domId;
    this.mode = config.mode || 'default';
    
    // Handle different modes
    if (this.mode === 'cookielist') {
      // Cookie list mode configuration
      const cookieSiteId = config.cookieSiteId;
      
      if (!cookieSiteId) {
        console.error('PrivacySDK Error: cookieSiteId is required when mode is "cookielist"');
        console.error('Example: new PrivacySDK({ domId: "container", mode: "cookielist", cookieSiteId: "site123" })');
        throw new Error('cookieSiteId is required for cookielist mode');
      }
      
      this.cookieSiteId = cookieSiteId;
      
      // Allow manual API URL override or use automatic detection
      if (config.apiUrl) {
        this.apiUrl = config.apiUrl;
      } else {
        const apiOrigin = config.apiOrigin || getApiOrigin();
        this.apiUrl = `${apiOrigin}/sdk/consent/${cookieSiteId}`;
      }
      
      console.log('PrivacySDK initialized in cookielist mode with:', {
        cookieSiteId,
        apiUrl: this.apiUrl
      });
    } else {
      // Default privacy mode configuration
      const privacyId = config.privacyId || window.privacyid;

      if (!privacyId || !isValidUUID(privacyId)) {
        console.error('PrivacySDK: Invalid or missing privacyId. Script will not run.');
        return; // This will stop further initialization
      }
      
      this.privacyId = privacyId;

      // Allow manual API URL override or use automatic detection
      if (config.apiUrl) {
        this.apiUrl = config.apiUrl;
      } else {
        const apiOrigin = config.apiOrigin || getApiOrigin();
        // Walk the documented detection chain so <html lang>, ?lang=, and an explicit
        // config.lang flow through to the backend. When none is found we omit the
        // langCode segment entirely and the server falls back to the lang cookie /
        // Accept-Language / notice DefaultLanguageCode.
        const lang = detectLanguage(config.lang);
        const identifier = config.identifier || 'anon';
        this.apiUrl = lang
          ? `${apiOrigin}/v1/privacy/${privacyId}/${encodeURIComponent(identifier)}/${encodeURIComponent(lang)}`
          : `${apiOrigin}/v1/privacy/${privacyId}`;
      }

      console.log('PrivacySDK initialized with:', {
        privacyId,
        apiUrl: this.apiUrl
      });
    }
    

    this.styleConfig = config.styles || {};
    this.popupStyle = {
      width: '500px',
      overlay: {
        color: 'rgba(0,0,0,0.5)'
      },
      ...config.popupStyle
    };

    // Extract website styles
    this.websiteStyles = this.extractWebsiteStyles();
    
    // Set up a MutationObserver to watch for the button
    this.setupButtonObserver();
  }

  extractWebsiteStyles() {
    const styles = {
      colors: {},
      fonts: {},
      buttons: {}
    };

    // Get body styles
    const computedBody = window.getComputedStyle(document.body);
    styles.colors.background = computedBody.backgroundColor;
    styles.colors.text = computedBody.color;
    styles.fonts.family = computedBody.fontFamily;
    styles.fonts.size = computedBody.fontSize;

    // Get button styles from existing buttons
    const sampleButton = document.querySelector('button');
    if (sampleButton) {
      const computedButton = window.getComputedStyle(sampleButton);
      styles.buttons = {
        background: computedButton.backgroundColor,
        color: computedButton.color,
        border: computedButton.border,
        borderRadius: computedButton.borderRadius,
        padding: computedButton.padding,
        fontSize: computedButton.fontSize
      };
    }

    return styles;
  }

  setupButtonObserver() {
    console.log('Setting up button observer with config:', this.styleConfig);
    
    // Create an observer instance
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        console.log('Mutation detected:', mutation.type, mutation.target);
        const button = document.getElementById('reddotCookieShowBtn');
        if (button) {
          console.log('Button found, current style:', {
            background: button.style.backgroundColor,
            color: button.style.color,
            computedStyle: window.getComputedStyle(button)
          });
          
          // Check if styles have been removed or changed
          const hasOurStyles = button.style.backgroundColor === (this.styleConfig.buttonStyle?.backgroundColor || '');
          console.log('Has our styles?', hasOurStyles, 'Expected:', this.styleConfig.buttonStyle?.backgroundColor);
          
          if (!hasOurStyles) {
            console.log('Reapplying styles...');
            this.applyButtonStyles();
          }
        }
      });
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, { 
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // Also try to apply styles immediately in case button already exists
    this.applyButtonStyles();
  }

  async load() {
    // Early check for DOM element
    const container = document.getElementById(this.domId);
    if (!container) {
      console.error(`PrivacySDK Error: Cannot find DOM element with id "${this.domId}"`);
      console.error('Please ensure:');
      console.error(`1. An element with id="${this.domId}" exists in your HTML`);
      console.error('2. The SDK is initialized after the DOM is loaded');
      console.error('3. The domId in your configuration matches the element id exactly');
      console.error('Example HTML: <div id="' + this.domId + '"></div>');
      throw new Error(`DOM element with id "${this.domId}" not found. Check the console for details.`);
    }
    
    try {
      const response = await fetch(this.apiUrl);
      console.log(response);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Handle different response formats based on mode
      if (this.mode === 'cookielist') {
        // For cookielist mode, the API returns HTML directly
        const htmlContent = await response.text();
        if (!htmlContent) {
          throw new Error('Invalid response format: missing cookie list data');
        }
        // Pass the HTML content directly for cookielist mode
        this.render(htmlContent);
      } else {
        // Default privacy mode response handling (JSON)
        const data = await response.json();
        console.log('Privacy mode response data:', data);
        
        // Check if the response has the expected structure
        if (data.result?.consentPopup) {
          this.render(data.result.consentPopup);
        } else if (data.consentPopup) {
          // Maybe the structure is different
          this.render(data.consentPopup);
        } else if (data.into || data.html || data.content) {
          // Maybe the data is directly in the response
          this.render(data);
        } else {
          console.error('Unexpected response structure:', data);
          throw new Error('Invalid response format: missing expected data structure');
        }
      }
    } catch (error) {
      console.error('Failed to load privacy content:', error);
      throw error;
    }
  }

  setStyles(styleConfig) {
    this.styleConfig = { ...this.styleConfig, ...styleConfig };
    // Re-render if content exists
    const container = document.getElementById(this.domId);
    if (container && container.innerHTML) {
      this.applyStyles(container);
    }
  }

  extractBodyContent(htmlString) {
    const match = /<body[^>]*>([\s\S]*)<\/body>/i.exec(htmlString);
    return match ? match[1] : htmlString;
  }

  render(data) {
    const container = document.getElementById(this.domId);
    if (!container) {
      console.error(`PrivacySDK Error: Cannot find DOM element with id "${this.domId}"`);
      console.error('Please ensure:');
      console.error(`1. An element with id="${this.domId}" exists in your HTML`);
      console.error('2. The SDK is initialized after the DOM is loaded');
      console.error('3. The domId in your configuration matches the element id exactly');
      throw new Error(`DOM element with id "${this.domId}" not found. Check the console for details.`);
    }

    console.log('Rendering with data:', data);

    // Create wrapper for content
    const wrapper = document.createElement('div');
    wrapper.className = this.styleConfig.customClasses?.container || 'privacy-sdk-container';

      // Create content element
      let content;
      if (this.mode === 'cookielist') {
        // For cookielist mode, handle the data structure appropriately
        content = document.createElement('div');
        content.className = 'cookie-list-content';
        // Check if data has HTML content or needs to be processed differently
        if (typeof data === 'string') {
          content.innerHTML = this.extractBodyContent(data);
        } else if (data.html) {
          content.innerHTML = this.extractBodyContent(data.html);
        } else if (data.content) {
          content.innerHTML = this.extractBodyContent(data.content);
        } else if (data.into) {
          content.innerHTML = this.extractBodyContent(data.into);
        } else {
          // If data is an object without known content fields, stringify it for debugging
          console.warn('Cookielist mode: Unknown data structure', data);
          content.innerHTML = '<pre>' + JSON.stringify(data, null, 2) + '</pre>';
        }
      } else {
        // Default privacy mode
        content = document.createElement('div');
        content.className = 'privacy-content';
        
        // Check various possible data structures
        if (data.into) {
          content.innerHTML = this.extractBodyContent(data.into);
        } else if (data.html) {
          content.innerHTML = this.extractBodyContent(data.html);
        } else if (data.content) {
          content.innerHTML = this.extractBodyContent(data.content);
        } else if (typeof data === 'string') {
          content.innerHTML = this.extractBodyContent(data);
        } else {
          console.error('Privacy mode: Unknown data structure', data);
          throw new Error('Invalid data structure for privacy mode. Expected data.into, data.html, or data.content');
        }
      }

      // Handle popup mode
      if (this.mode === 'popup' && content) {
      // Create overlay
      const overlay = document.createElement('div');
      overlay.className = 'privacy-overlay';
      Object.assign(overlay.style, {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: this.popupStyle.overlay.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999
      });

      // Create popup container with website-matching styles
      const popup = document.createElement('div');
      popup.className = 'privacy-popup';
      Object.assign(popup.style, {
        width: this.popupStyle.width,
        maxWidth: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        backgroundColor: this.websiteStyles.colors.background || '#fff',
        color: this.websiteStyles.colors.text || '#333',
        fontFamily: this.websiteStyles.fonts.family,
        fontSize: this.websiteStyles.fonts.size,
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        position: 'relative',
        animation: 'privacyPopupFadeIn 0.3s ease-out'
      });

      // Add close button
      const closeButton = document.createElement('button');
      closeButton.className = 'privacy-popup-close';
      closeButton.innerHTML = '×';
      Object.assign(closeButton.style, {
        position: 'absolute',
        top: '10px',
        right: '10px',
        border: 'none',
        background: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        color: 'inherit',
        padding: '5px',
        zIndex: 1
      });
      closeButton.onclick = () => overlay.remove();
      popup.appendChild(closeButton);

      // Add title for popup with website-matching styles
      if (data.title) {
        const title = document.createElement('h2');
        title.className = 'privacy-popup-title';
        title.innerHTML = data.title;
        Object.assign(title.style, {
          margin: '0 0 20px',
          padding: '20px',
          borderBottom: '1px solid rgba(0,0,0,0.1)',
          fontSize: '1.5em',
          fontWeight: '600'
        });
        popup.appendChild(title);
      }

      // Add content container with padding
      const contentContainer = document.createElement('div');
      contentContainer.style.padding = '20px';
      contentContainer.appendChild(content.cloneNode(true));
      popup.appendChild(contentContainer);

      // Add buttons container for popup with website-matching styles
      const buttonContainer = document.createElement('div');
      buttonContainer.className = 'privacy-popup-buttons';
      Object.assign(buttonContainer.style, {
        padding: '20px',
        borderTop: '1px solid rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px'
      });

      // Add buttons with website-matching styles
      if (data.submit) {
        const submitBtn = document.createElement('button');
        submitBtn.className = 'privacy-popup-submit';
        submitBtn.textContent = data.submit;
        Object.assign(submitBtn.style, {
          ...this.websiteStyles.buttons,
          padding: '10px 20px',
          cursor: 'pointer',
          fontWeight: '500'
        });
        buttonContainer.appendChild(submitBtn);
      }

      if (data.cancel) {
        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'privacy-popup-cancel';
        cancelBtn.textContent = data.cancel;
        Object.assign(cancelBtn.style, {
          ...this.websiteStyles.buttons,
          padding: '10px 20px',
          cursor: 'pointer',
          fontWeight: '500',
          backgroundColor: 'transparent',
          border: '1px solid currentColor'
        });
        cancelBtn.onclick = () => overlay.remove();
        buttonContainer.appendChild(cancelBtn);
      }

      popup.appendChild(buttonContainer);

      // Add footer for popup
      if (data.footer) {
        const footer = document.createElement('div');
        footer.className = 'privacy-popup-footer';
        footer.innerHTML = data.footer;
        Object.assign(footer.style, {
          padding: '20px',
          borderTop: '1px solid rgba(0,0,0,0.1)',
          fontSize: '0.9em',
          color: 'rgba(0,0,0,0.6)'
        });
        popup.appendChild(footer);
      }

      // Add animation keyframes
      const styleSheet = document.createElement('style');
      styleSheet.textContent = `
        @keyframes privacyPopupFadeIn {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `;
      document.head.appendChild(styleSheet);

      // Append popup to overlay instead of wrapper
      overlay.appendChild(popup);
      document.body.appendChild(overlay);
    }

    // Handle script tags to ensure they execute
    const scripts = wrapper.getElementsByTagName('script');
    Array.from(scripts).forEach(oldScript => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      newScript.appendChild(document.createTextNode(oldScript.innerHTML));
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });

    // For non-popup mode or as fallback, append content to wrapper
    if (this.mode !== 'popup' && content) {
      wrapper.appendChild(content);
    }

    // Clear container and append wrapper (will be empty in popup mode)
    container.innerHTML = '';
    container.appendChild(wrapper);

    // Apply styles
    this.applyStyles(container);
  }

  applyStyles(container) {
    const wrapper = container.firstChild;
    if (!wrapper) return;

    // Apply container styles
    if (this.styleConfig.containerStyle) {
      Object.assign(wrapper.style, this.styleConfig.containerStyle);
    }

    // Apply content styles
    if (this.styleConfig.contentStyle) {
      Array.from(wrapper.children).forEach(element => {
        Object.assign(element.style, this.styleConfig.contentStyle);
      });
    }

    // Apply custom classes
    if (this.styleConfig.customClasses) {
      if (this.styleConfig.customClasses.container) {
        wrapper.className = this.styleConfig.customClasses.container;
      }
      if (this.styleConfig.customClasses.content) {
        Array.from(wrapper.children).forEach(element => {
          element.className += ` ${this.styleConfig.customClasses.content}`;
        });
      }
    }

    // Re-apply button styles
    this.applyButtonStyles();
  }

  applyButtonStyles() {
    console.log('Applying button styles...');
    const button = document.getElementById('reddotCookieShowBtn');
    if (!button) {
      console.log('Button not found');
      return;
    }

    console.log('Current button state:', {
      element: button,
      className: button.className,
      style: button.style,
      computedStyle: window.getComputedStyle(button)
    });

    // Create a new style element for our button
    const styleId = 'reddot-cookie-button-styles';
    let styleEl = document.getElementById(styleId);
    
    if (!styleEl) {
      console.log('Creating style element');
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }

    // Create CSS rules with !important to ensure they take precedence
    const cssRules = [];
    
    // Base styles without positioning
    cssRules.push(`
      #reddotCookieShowBtn {
        display: inline-block !important;
        margin: 5px !important;
      }
    `);

    // Add custom styles
    if (this.styleConfig.buttonStyle) {
      console.log('Generating CSS rules from:', this.styleConfig.buttonStyle);
      const styles = Object.entries(this.styleConfig.buttonStyle)
        .map(([key, value]) => {
          const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
          return `${cssKey}: ${value} !important;`;
        })
        .join('\n');
      cssRules.push(`
        #reddotCookieShowBtn {
          ${styles}
          /* Additional overrides for button appearance */
          box-sizing: border-box !important;
          text-decoration: none !important;
          font-family: inherit !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
        }
      `);
    }

    // Add hover effect
    cssRules.push(`
      #reddotCookieShowBtn:hover {
        opacity: 0.9 !important;
        cursor: pointer !important;
      }
    `);

    // Update the style element and ensure it's loaded after other styles
    styleEl.textContent = cssRules.join('\n');
    document.head.appendChild(styleEl); // Re-append to ensure it's last
    console.log('Applied CSS rules:', styleEl.textContent);

    // Apply custom class if specified
    if (this.styleConfig.customClasses?.button) {
      const existingClasses = button.className.split(' ').filter(c => c.trim());
      const newClass = this.styleConfig.customClasses.button;
      if (!existingClasses.includes(newClass)) {
        button.className = [...existingClasses, newClass].join(' ');
      }
    }
  }
}

export default PrivacySDK;
